import { useCallback } from "react";
import { KycStatus, MaritalStatus, WorkingPermitType } from "../__generated__/graphql";
import { UserFieldsFragment } from "../__generated__/operations/graphql";
import { getAge } from "../utils";

/** A registered partnership is treated as a marriage; a separated worker has no partner to declare. */
export const hasPartner = (maritalStatus?: MaritalStatus | null) =>
    maritalStatus === MaritalStatus.Married ||
    maritalStatus === MaritalStatus.RegisteredPartnership;

/**
 * A minor always counts for withholding tax; from 18, only while in a first education. The children
 * form holds the Date its date field picked, the user data an ISO string.
 */
export const isAdult = (birthdate?: string | Date | null) =>
    !!birthdate && getAge(new Date(birthdate)) >= 18;

export const getWorkerProfileIsComplete = (user?: UserFieldsFragment | null) => {
    const kycVerified = user?.kycStatus?.status === KycStatus.Accepted;
    const maritalStatus = user?.workerProfile?.maritalStatus;

    const avsNumber = user?.workerProfile?.avsNumber;

    const children = user?.workerProfile?.children || [];

    const bankAccount = user?.workerProfile?.bankAccount;

    const workPermit = user?.workerProfile?.lastWorkPermit;

    const hasChildren = user?.workerProfile?.hasChildren;

    const partner = user?.workerProfile?.partner;

    const curriculumVitae = user?.workerProfile?.curriculumVitae;

    const isPartnered = hasPartner(maritalStatus);

    const address = user?.address;

    const nationality = user?.nationality;

    // An unknown address is taken as Swiss: the home address step asks for it, and the answers
    // that depend on it are asked again once it is known.
    const livesAbroad =
        !!address?.locality?.state?.country?.code && address.locality.state.country.code !== "CH";

    // Swiss workers and permit C holders living in Switzerland are taxed normally; everyone else
    // has the tax taken from their salary at the source, whose amount depends on the answers below.
    const paysWithholdingTax =
        livesAbroad || (nationality !== "CH" && workPermit?.type !== WorkingPermitType.PermitC);

    const churchMember = user?.workerProfile?.churchMember;

    // The partner's nationality and permit are only asked to workers living in Switzerland; a Swiss
    // partner has no permit.
    const isPartnerDetailsMissing =
        !livesAbroad &&
        (!partner?.nationality || (partner.nationality !== "CH" && !partner.permitType));

    const isFiscalAttestationRequired = workPermit?.type === WorkingPermitType.PermitG;

    const hasFiscalAttestation = user?.workerProfile?.hasFiscalAttestation;

    const email = user?.email;

    const emailVerified = user?.verified;

    const missingInformations: string[] = [];

    if (!kycVerified) {
        missingInformations.push("kyc");
    }
    if (!bankAccount) {
        missingInformations.push("bank_account");
    }
    if (
        paysWithholdingTax &&
        (hasChildren === null ||
            children.some((child) => isAdult(child.birthdate) && child.inFirstEducation === null))
    ) {
        // A child turning 18 reopens the step, to ask whether they are still in a first education.
        missingInformations.push("children");
    }
    if (
        !maritalStatus ||
        (paysWithholdingTax &&
            (churchMember === null || (isPartnered && (!partner || isPartnerDetailsMissing))))
    ) {
        // AVS is no longer part of this check: the civil_state form cannot be saved
        // without maritalStatus, and it now allows an empty AVS (foreigner exemption or
        // the "no AVS number" toggle), so a set maritalStatus implies the AVS rule passed.
        //
        // The weekly residence canton is not checked: null is also the answer of a worker who goes
        // home every night. The form asks it together with the church question, so an answered
        // church question means it was answered too.
        missingInformations.push("civil_state");
    }
    if (isFiscalAttestationRequired && hasFiscalAttestation === null) {
        missingInformations.push("fiscal_attestation");
    }
    if (!email || !emailVerified) {
        // Setting the address and verifying it are a single step: an unset address cannot be
        // verified, and PostOnboardingPrepare renders one bullet per key, so splitting them would
        // list the same step twice.
        //
        // A verified address completes the step even when it is also the business email of one of
        // the user's employer profiles: the worker profile is not required to use a distinct
        // address.
        missingInformations.push("email");
    }
    if (!address) {
        missingInformations.push("home_address");
    }
    if (!curriculumVitae) {
        missingInformations.push("experiences");
    }

    return {
        isValid: missingInformations.length === 0,
        missingInformations,
        maritalStatus,
        bankAccount,
        children,
        hasChildren,
        workPermit,
        paysWithholdingTax,
        livesAbroad,
        isFiscalAttestationRequired,
        hasFiscalAttestation,
        email,
        emailVerified,
        address,
        avsNumber,
        curriculumVitae,
        kycVerified,
    };
};

export const useGetIsWorkerProfileComplete = () => {
    return useCallback(
        (...args: Parameters<typeof getWorkerProfileIsComplete>) =>
            getWorkerProfileIsComplete(...args),
        [],
    );
};
